import { Injectable } from '@angular/core';
import { DivIcon, LatLng, LatLngExpression, Map as LeafletMapType, MarkerCluster, ZoomPanOptions } from 'leaflet';

import { Observable } from 'rxjs';

import {
    ClusterStylingOptions,
    FitBoundsOptionsType,
    IconType,
    LatLngBoundsType,
    LatLngEventType,
    LatLngPolylineType,
    LatLngType,
    LayerGroupType,
    LayerStylesConfig,
    LayerType,
    MapLayersSettingsOption,
    MapOptionsType,
    MapType,
    MarkerClusterGroupType,
    MarkerOptionsType,
    MarkerType,
    PolylineOptionsType,
    PolylineType,
    PopupOptionsType,
    PopupType
} from './map.model';

@Injectable()
export abstract class MapApiService {
    /**
     * returns an observable of the map object
     */
    abstract nativeMap(): Observable<MapType>;

    /**
     * returns an observable of the map overlays options
     */
    abstract mapOverlays(): Observable<MapLayersSettingsOption[]>;

    /**
     * returns an observable of the map's ready state. Returns true if the map is ready and false otherwise.
     */
    abstract mapReady(): Observable<boolean>;

    /**
     * @param elementId the id of the html element where the map will be created
     * @param options map options
     */
    abstract createMap(
        elementId: string | HTMLElement,
        options?: MapOptionsType,
        hiddenMap?: boolean,
        mapStyles?: LayerStylesConfig[],
        hasViewSettingsControl?: boolean
    ): L.Map;

    /**
     * Removes the map from the dom element and sets map state to false
     */
    abstract destroyMap(): void;

    abstract addToMap(layer: LayerType): void;

    abstract addStopsToMap(): void;

    abstract removeStopsFromMap(): void;

    abstract applyLayers(layers: LayerType[]): void;

    abstract hasLayer(layer: LayerType): boolean;

    abstract drawPolyline(latlng: LatLngPolylineType, options?: PolylineOptionsType): PolylineType;

    abstract createPopup(content: any, option?: PopupOptionsType): PopupType;

    abstract openPopup(latlng: LatLngType, popup: PopupType): void;

    abstract closePopup(): void;

    abstract bindPopupToLayer(layer: LayerType, popup: PopupType): void;

    abstract createMarker(latlng: LatLngType, options?: MarkerOptionsType): MarkerType;

    abstract setMarkerIcon(marker: MarkerType, icon: IconType): void;

    abstract addPopupToMarker(marker: MarkerType, content: any): void;

    abstract createIcon(optons: any, divIcon?: boolean): IconType;

    abstract createLayerGroup(): LayerGroupType;

    abstract clearLayerGroup(layerGroup: LayerGroupType): void;

    abstract removeLayerGroup(layerGroup: LayerGroupType): void;

    abstract addToLayerGroup(layerGroup: LayerGroupType, layer: LayerType): void;

    abstract removeFromLayerGroup(layerGroup: LayerGroupType, layer: LayerType): void;

    abstract removeLayer(layer: LayerType): void;

    abstract createMarkerClusterGroup(options?: any): MarkerClusterGroupType;

    abstract createCustomClusterMarkers(cluster: MarkerCluster, options?: ClusterStylingOptions): DivIcon;

    abstract addToOverlays(name: string, label: string, layerGroup: LayerGroupType): void;

    abstract removeFromOverlays(name: string): void;

    abstract addToControls(label: string, layerGroup: LayerGroupType): void;

    abstract removeFromControls(layerGroup: LayerGroupType): void;

    abstract showControls(show: boolean): void;

    abstract initLocate(options);

    abstract getCenterCoordinates(): LatLng;

    abstract centerMapToCoordinates(latlng: LatLngExpression, zoom?: number, zoomOptions?: ZoomPanOptions): void;

    /**
     * Creates the click event listener that returns an observable of the coordinates clicked on the map
     */
    abstract createClickEventSubscription(): Observable<LatLngEventType>;

    abstract isWithinMapBounds(latlng: LatLngType): boolean;

    /**
     * Zoom the map to a specified bound.
     */
    abstract fitBounds(bounds: LatLngBoundsType, options?: FitBoundsOptionsType): void;

    abstract doResize(): void;

    abstract zoomIn(): void;

    abstract zoomOut(): void;

    abstract addListener(event: string, fn: () => void, context?: any): void;

    abstract addListenerWithEventCallback(event: string, fn: (event: any) => void): void;

    abstract hasEventListener(event: string): boolean;

    abstract addListenerSubscription(event: string): Observable<any>;

    abstract removeListener(event: string, fn: () => void, context?: any): void;

    abstract removeAllListeners(): void;

    abstract addClass(el: HTMLElement, name: string): void;

    abstract removeClass(el: HTMLElement, name: string): void;

    abstract getNativeMap(): LeafletMapType;

    abstract isCenteringEnabled(): boolean;

    abstract setCenteringEnabled(enabled: boolean): void;

    abstract checkIsContainBound(latlng: LatLng): boolean;

    /**
     * Adds a listener function that will be called only once when the event occurs
     */
    abstract once(event: string, fn: (event?: any) => void, context?: any): void;
}
